import React, { useState, useEffect } from 'react';
import { documentsApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  FolderOpen, Upload, FileText, Trash2, Shield, Lock, 
  Calendar, Download, Image, File
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [newDoc, setNewDoc] = useState({
    name: '',
    document_type: 'id',
    file_url: ''
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const data = await documentsApi.getAll();
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!newDoc.name || !newDoc.file_url) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await documentsApi.upload(newDoc);
      toast.success('Document uploaded successfully');
      setUploadOpen(false);
      setNewDoc({ name: '', document_type: 'id', file_url: '' });
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to upload document');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await documentsApi.delete(deleteId);
      toast.success('Document deleted');
      setDeleteId(null);
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const getDocIcon = (type) => {
    switch (type) {
      case 'id': return <Image className="h-5 w-5 text-blue-600" />;
      case 'proof': return <FileText className="h-5 w-5 text-green-600" />;
      case 'letter': return <File className="h-5 w-5 text-purple-600" />;
      default: return <FileText className="h-5 w-5 text-slate-600" />;
    }
  };

  const getDocTypeLabel = (type) => {
    switch (type) {
      case 'id': return 'ID Document';
      case 'proof': return 'Proof Document';
      case 'letter': return 'Letter';
      case 'credit_report': return 'Credit Report';
      default: return 'Other';
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-1/4" />
        <div className="h-48 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Document Vault
          </h1>
          <p className="text-slate-600 mt-1">Securely store and manage your documents.</p>
        </div>
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button className="btn-teal gap-2" data-testid="upload-btn">
              <Upload className="h-4 w-4" />
              Upload Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="upload-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>Upload Document</DialogTitle>
              <DialogDescription>
                Add a document to your secure vault.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Document Name</Label>
                <Input
                  placeholder="e.g., Driver's License"
                  value={newDoc.name}
                  onChange={(e) => setNewDoc(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="doc-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select 
                  value={newDoc.document_type}
                  onValueChange={(value) => setNewDoc(prev => ({ ...prev, document_type: value }))}
                >
                  <SelectTrigger data-testid="doc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="id">ID Document</SelectItem>
                    <SelectItem value="proof">Proof Document</SelectItem>
                    <SelectItem value="letter">Letter</SelectItem>
                    <SelectItem value="credit_report">Credit Report</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>File URL</Label>
                <Input
                  placeholder="https://..."
                  value={newDoc.file_url}
                  onChange={(e) => setNewDoc(prev => ({ ...prev, file_url: e.target.value }))}
                  data-testid="doc-url-input"
                />
                <p className="text-xs text-slate-500">
                  Enter the URL of your uploaded file (e.g., from cloud storage)
                </p>
              </div>
              <Button 
                className="w-full btn-teal"
                onClick={handleUpload}
                disabled={!newDoc.name || !newDoc.file_url}
                data-testid="save-doc-btn"
              >
                Upload Document
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Security Notice */}
      <Card className="border-teal-200 bg-teal-50" data-testid="security-notice">
        <CardContent className="flex items-center gap-3 py-4">
          <Shield className="h-5 w-5 text-teal-600" />
          <div className="flex-1">
            <span className="text-teal-800 font-medium">Your documents are protected</span>
            <p className="text-sm text-teal-700">
              All documents are encrypted at rest and only accessible by you.
            </p>
          </div>
          <Lock className="h-5 w-5 text-teal-600" />
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card className="border-slate-100 shadow-sm" data-testid="documents-list">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Your Documents</CardTitle>
          <CardDescription>
            {documents.length} document{documents.length !== 1 ? 's' : ''} stored
          </CardDescription>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FolderOpen className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <p className="font-medium text-slate-700">No documents yet</p>
              <p className="text-sm mt-1">Upload IDs, proof documents, and letters to keep them secure.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div 
                  key={doc.document_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  data-testid={`document-${doc.document_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                      {getDocIcon(doc.document_type)}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{doc.name}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <span>{getDocTypeLabel(doc.document_type)}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(doc.uploaded_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => window.open(doc.file_url, '_blank')}
                      data-testid={`view-doc-${doc.document_id}`}
                    >
                      <Download className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => setDeleteId(doc.document_id)}
                      data-testid={`delete-doc-${doc.document_id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The document will be permanently deleted from your vault.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
